import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Flex, Spinner, Text, VStack } from '@chakra-ui/react';
import { useAuth } from '../features/auth/AuthProvider';

interface AuthGuardProps {
  children: React.ReactNode;
  requiredRoles?: string[];
}

export function AuthGuard({ children, requiredRoles }: AuthGuardProps) {
  const { user, admin, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <Flex flex={1} justify="center" align="center" data-testid="auth-guard-loading" minH="100vh">
        <VStack>
          <Spinner size="lg" />
          <Text mt={2}>Loading...</Text>
        </VStack>
      </Flex>
    );
  }

  // Not signed in → redirect to login
  if (!user || user.expired) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Authenticated but admin profile not loaded yet — wait for sync
  if (!admin) {
    return (
      <Flex flex={1} justify="center" align="center" data-testid="auth-guard-loading" minH="100vh">
        <VStack>
          <Spinner size="lg" />
          <Text mt={2}>Loading profile...</Text>
        </VStack>
      </Flex>
    );
  }

  // Admin profile loaded but inactive
  if (!admin.isActive) {
    return <Navigate to="/login" replace />;
  }

  // MFA is enforced by Zitadel during the hosted login flow now — if Zitadel
  // returned a token, the user already passed any required MFA. The local
  // mfaRequired flag is retained on AdminProfile for backwards-compatibility
  // but is treated as informational rather than a redirect trigger.

  // Role check
  if (requiredRoles && !requiredRoles.includes(admin.role)) {
    return (
      <Flex flex={1} justify="center" align="center" data-testid="auth-guard-forbidden" minH="100vh">
        <VStack>
          <Text color="red.500" fontSize="xl">Access Denied</Text>
          <Text color="gray.600" mt={2}>You don't have permission to access this page.</Text>
        </VStack>
      </Flex>
    );
  }

  return <>{children}</>;
}
